import React from 'react';
import type { ListProps, ListRowProps, ListRowRenderer, Size } from 'react-virtualized';
import { AutoSizer } from 'react-virtualized/dist/es/AutoSizer';
import { List } from 'react-virtualized/dist/es/List';
import { Select, MenuItem, FormControl } from '@material-ui/core';
import FlatCard, { FlatCardProps } from './FlatCard/FlatCard';
import {
  companionListViewStyle,
  flatCardStyle,
  cardHeight,
  dropDownStyle,
  menuListStyle,
  levelByDropDownStyle,
  dropdownSelectedValue,
  selectIconClass,
} from './CompanionListView.styles';
import { KeyVal } from '../../common/object';
import { isNil, isEqual, isEmpty } from 'lodash';
import { CompanionDataLookup } from 'src/utils/Component/ListView';
import { BasicViewItem } from 'configuration-schemas/viewdefns/general';
import { Skeleton } from '@material-ui/lab';
import { SortByDirection } from 'src/components/Subheader/Subheader.types';

/**
 * @deprecated
 * This type should no longer be referenced.
 * Instead please refer to the types BasicViewItem for sort items and GroupViewItem for grouping.
 * TODO: Remove type where referenced in favor of BasicViewItem
 * */
export type SortOption = Pick<BasicViewItem, 'text' | 'dataIndex'>;

export interface ScrollTo {
  eventId: number;
  where: KeyVal<string, any>;
}

export type CompanionFieldKey = 'title' | 'name' ;
export type CompanionFieldClasses = Partial<Record<CompanionFieldKey, string[]>>;

/**
 * Represents a companion list item
 *
 * @interface ListViewable
 * @property {string} id - Configured 'title' field.
 * @property {string} name - Configured 'body' of companion view.
 * @property {number|null} [stars] - Optional star rating, nullable if not rated.
 * @property {string} [imageUri] - Optional URI of the image associated with the item.
 * @property {string} [title] - Configured displayTitle, because id & name desynced and has to be specified separately.
 */
export interface ListViewable {
  id: string;
  name: string;
  stars: number | null;
  imageUri?: string;
  title?: string;
  // Optional styling classes for fields in companion list
  fieldClasses?: CompanionFieldClasses;
  [key: string]: any | undefined;
}

// here so CompanionList can accept the same inputs for collapse state management
export interface CollapsibleCompanion {
  isCompanionCollapsed: boolean;
  onToggleCompanion(): void;
}

export interface Props extends CollapsibleCompanion {
  defaultSelection: number;
  sortOptions: SortOption[];
  data: ListViewable[];
  isDataloaded: boolean;
  label: string;
  className: string;
  scrollTo?: ScrollTo;
  noImageUrl?: string;
  selectedIndex?: number;
  searchComponent?: JSX.Element;
  filterComponent?: JSX.Element;
  initialSortDirection?: SortByDirection;
  levelOptions?: SortOption[];
  defaultLevelSelection?: number;
  hoverListItemElement?: JSX.Element;
  dataLookup?: CompanionDataLookup;
  onListItemClicked?(id: string, event?: React.MouseEvent<HTMLDivElement, MouseEvent>): void;
  onSortSelection?(sortOption: SortOption): void;
  onChangeDirection?(direction: SortByDirection): void;
  onLevelSelection?(levelOption: SortOption): void;
}

export interface State {
  direction: SortByDirection;
  selection: number;
  levelSelection: number;
}

const NoRowsRenderer = () => <div className="no-item">No Items To Show</div>;

export default class CompanionListView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      direction: isNil(props.initialSortDirection) ? 'desc' : props.initialSortDirection,
      selection: props.defaultSelection,
      levelSelection: props.defaultLevelSelection || 0,
    };
    this.selectDropdown = this.selectDropdown.bind(this);
    this.toggleDirection = this.toggleDirection.bind(this);
    this.toggleCollapse = this.toggleCollapse.bind(this);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.defaultSelection != prevProps.defaultSelection) {
      this.setState({
        selection: this.props.defaultSelection,
      });
    }
    if (this.props.initialSortDirection != prevProps.initialSortDirection && this.props.initialSortDirection) {
      this.setState({
        direction: this.props.initialSortDirection,
      });
    }
    if (!isEqual(this.props.levelOptions, prevProps.levelOptions)) {
      this.setState({
        levelSelection: 0,
      });
    }
    // Update level selection to default if (and only if) the default changed **AND** the previous was
    // invalid (sub-zero). This can happen when list view is rendered prior to the config loading in on page
    // navigation.
    const newDefaultLev = this.props.defaultLevelSelection;
    const prevDefaultLev = prevProps.defaultLevelSelection;
    if (isNil(prevDefaultLev) || prevDefaultLev < 0) {
      if (!isNil(newDefaultLev) && newDefaultLev >= 0) {
        this.setState({
          levelSelection: newDefaultLev,
        });
        if (this.props.onLevelSelection && this.props.levelOptions) {
          this.props.onLevelSelection(this.props.levelOptions[newDefaultLev]);
        }
      }
    }
  }

  selectDropdown(selection: number) {
    this.setState({
      selection,
    });

    if (this.props.onSortSelection && this.props.sortOptions != null && selection > -1) {
      this.props.onSortSelection(this.props.sortOptions[selection]);
    }
  }

  selectLevelDropdown(selection: number) {
    this.setState({
      levelSelection: selection,
    });

    if (this.props.onLevelSelection && this.props.levelOptions) {
      this.props.onLevelSelection(this.props.levelOptions[selection]);
    }
  }

  toggleCollapse() {
    if (this.props.onToggleCompanion) {
      this.props.onToggleCompanion();
    }
  }

  toggleDirection() {
    const direction = this.state.direction === 'asc' ? 'desc' : 'asc';
    this.setState({ direction });

    if (this.props.onChangeDirection) {
      this.props.onChangeDirection(direction);
    }
  }

  listRowRenderer: ListRowRenderer = ({ index, style }: ListRowProps) => {
    const { data, dataLookup, hoverListItemElement, isCompanionCollapsed, onListItemClicked, selectedIndex } =
      this.props;
    const hideStars = dataLookup && dataLookup.starsId === null;
    const noImageUrl = dataLookup && dataLookup.imageUrlId ? this.props.noImageUrl : '';

    const datum = data[index];
    if (hideStars) datum.stars = null;

    const cardClasses =
      flatCardStyle + (selectedIndex === index ? ' scroll-target' : '') + (isCompanionCollapsed ? ' collapsed' : '');

    const flatCardProps: FlatCardProps = {
      ...datum,
      noImageUrl,
      index,
      className: cardClasses,
      hoverElement: hoverListItemElement,
      cardHeight,
      onClick: onListItemClicked,
    };

    return (
      <div style={style} key={index}>
        <FlatCard {...flatCardProps} />
      </div>
    );
  };

  skeletonRowRenderer: ListRowRenderer = ({ index, style }: ListRowProps) => {
    const { isCompanionCollapsed } = this.props;

    const rowContent = isCompanionCollapsed ? (
      <Skeleton animation="wave" variant="rect" height="100%" style={{ margin: '5px 10px' }} />
    ) : (
      <div className="companion-list-skeleton-container" data-qa= "companion-list-skeleton-container">
        <div className="companion-left-skeleton-container">
          <Skeleton animation="wave" variant="text" width="70%" />
          <Skeleton animation="wave" variant="rect" height="50%" />
          <Skeleton animation="wave" variant="text" width="70%" height="20%" />
        </div>
        <div className="companion-right-skeleton-container">
          <Skeleton animation="wave" variant="rect" height="100%" width="100%" />
        </div>
      </div>
    );

    return (
      <div style={{ ...style, height: '115px' }} key={index}>
        {rowContent}
      </div>
    );
  };

  render() {
    const {
      sortOptions,
      levelOptions,
      data,
      className,
      scrollTo,
      label = 'Count',
      searchComponent,
      filterComponent,
      isCompanionCollapsed,
      isDataloaded,
      selectedIndex
    } = this.props;
    const { selection: orderBySelection, levelSelection } = this.state;

    const fallbackIndex = !isNil(scrollTo)
      ? data.findIndex((datum) => datum[scrollTo.where.key] === scrollTo.where.value)
      : undefined;
    const scrollToIndex = selectedIndex ?? fallbackIndex;

    const listOptions: ListProps = {
      overscanRowCount: 10,
      noRowsRenderer: NoRowsRenderer,
      rowHeight: cardHeight,
      rowRenderer: !isDataloaded ? this.skeletonRowRenderer : this.listRowRenderer,
      rowCount: !isDataloaded ? 4 : isEmpty(data) ? 0 : data.length,
      tabIndex: -1,
      scrollToAlignment: 'start',
      scrollToIndex,
      // Must be overwritten by autosizer, this is just type error avoidance
      height: NaN,
      width: NaN,
    };

    const classes1 =
      companionListViewStyle +
      ' companion-list-view' +
      (className ? ` ${className}` : '') +
      (isCompanionCollapsed ? ' collapsed' : '');

    const isAsc = this.state.direction === 'asc';
    const directionClasses = `sort-dir far fa-arrow-up ${!isAsc ? 'rotated' : ''}`;

    function renderList(size: Size) {
      const options = {
        ...listOptions,
        ...size,
        //React-Virtualized will shallow‐compare it and rerender when it changes
        data,
      };
      return <List {...options} />;
    }

    let controls;
    if (!isEmpty(sortOptions) && orderBySelection >= 0) {
      controls = (
        <React.Fragment>
          <FormControl classes={{ root: dropDownStyle(!isCompanionCollapsed) }}>
            <Select
              classes={{ icon: selectIconClass }}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                classes: { paper: menuListStyle },
                getContentAnchorEl: null,
              }}
              disableUnderline={true}
              value={sortOptions[orderBySelection].text}
              renderValue={(value) => (
                <div
                  style={
                    isCompanionCollapsed ? { ...dropdownSelectedValue } : { ...dropdownSelectedValue, width: '100%' }
                  }
                >
                  {value}
                </div>
              )}
            >
              {sortOptions.map((option, index) => (
                <MenuItem key={index} value={option.text} onClick={() => this.selectDropdown(index)}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span
            className={directionClasses}
            style={{ position: 'relative', right: !isCompanionCollapsed ? -5 : -3 }}
            onClick={this.toggleDirection}
          />
        </React.Fragment>
      );
    }

    const collapsedControls = isCompanionCollapsed && controls ? controls : <span className="spacer" />;
    const expandControls = isCompanionCollapsed && controls ? null : controls;
    let expandLevelControls;
    if (!isCompanionCollapsed && levelOptions) {
      expandLevelControls = (
        <div className="control-bar level-bar">
          <label>Level:</label>
          <FormControl classes={{ root: levelByDropDownStyle }}>
            <Select
              classes={{ icon: selectIconClass }}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                classes: { paper: menuListStyle },
                getContentAnchorEl: null,
              }}
              disableUnderline={true}
              value={levelSelection}
            >
              {levelOptions.map((option, index) => (
                <MenuItem key={index} value={index} onClick={() => this.selectLevelDropdown(index)}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <span style={{ width: 17 }} />
        </div>
      );
    }

    return (
      <div className={classes1}>
        <header style={{ padding: isCompanionCollapsed ? '1rem 0.5rem' : '1rem' }}>
          <div className="expander-container">
            <span className="label">
              {label}: {data.length}
            </span>
            <span className="expander" onClick={this.toggleCollapse}>
              &#60;
            </span>
          </div>
          <div style={{ display: 'flex' }}>{collapsedControls}</div>

          <div className="extra-controls">
            {!isNil(searchComponent) && <div className="search">{searchComponent}</div>}
            {!isNil(filterComponent) && <div className="filter">{filterComponent}</div>}
          </div>
          <div className="control-bar">
            <label>Order By:</label>
            {expandControls}
          </div>
          {expandLevelControls}
        </header>
        <div className={isEmpty(this.props.data) && this.props.isDataloaded ? 'no-items-container' : 'list-container'}>
          <AutoSizer>{renderList}</AutoSizer>
        </div>
      </div>
    );
  }
}
